import { useEffect, useRef } from 'react'

type GestureCallbacks = {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeMove?: (_direction: 'left' | 'right', _progress: number) => void
  onSwipeCancel?: () => void
}

export const useGesture = (
  onSwipeLeftOrCallbacks: (() => void) | GestureCallbacks,
  onSwipeRight?: () => void
) => {
  const xDown = useRef<number | null>(null)
  const yDown = useRef<number | null>(null)
  const isHorizontalLock = useRef<boolean>(false)
  const THRESHOLD = 80

  const callbacks: GestureCallbacks = 
    typeof onSwipeLeftOrCallbacks === 'function' 
      ? { onSwipeLeft: onSwipeLeftOrCallbacks, onSwipeRight } 
      : onSwipeLeftOrCallbacks

  useEffect(() => {
    function handleTouchStart(evt: TouchEvent) {
      const firstTouch = evt.touches[0]
      xDown.current = firstTouch.clientX
      yDown.current = firstTouch.clientY
      isHorizontalLock.current = false
    }

    function handleTouchMove(evt: TouchEvent) {
      if (!xDown.current || !yDown.current) return

      const xUp = evt.touches[0].clientX
      const yUp = evt.touches[0].clientY

      const xDiff = xDown.current - xUp
      const yDiff = yDown.current - yUp

      // Lock direction if movement is significant
      if (!isHorizontalLock.current && Math.abs(xDiff) > 10) {
        if (Math.abs(xDiff) > Math.abs(yDiff)) {
          isHorizontalLock.current = true
        }
      }

      if (isHorizontalLock.current) {
        // Prevent vertical scrolling once we're swiping
        if (evt.cancelable) evt.preventDefault()

        const direction = xDiff > 0 ? 'right' : 'left'
        const progress = Math.min(Math.abs(xDiff) / THRESHOLD, 1.2) // allow slight over-drag
        callbacks.onSwipeMove?.(direction, progress)
      }
    }

    function handleTouchEnd(evt: TouchEvent) {
      if (!xDown.current || !yDown.current) return

      const xUp = evt.changedTouches[0].clientX
      const xDiff = xDown.current - xUp

      if (isHorizontalLock.current && Math.abs(xDiff) >= THRESHOLD) {
        if (xDiff > 0) {
          callbacks.onSwipeRight?.()
        } else {
          callbacks.onSwipeLeft?.()
        }
      } else if (isHorizontalLock.current) {
        callbacks.onSwipeCancel?.()
      }

      xDown.current = null
      yDown.current = null
      isHorizontalLock.current = false
    }

    // Passive false is required to allow preventDefault for direction locking
    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [callbacks])
}
